import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Users, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useState, useMemo } from 'react';
import { AddressDisplay } from '@/components/shared/HexDisplay';
import { useActiveValidators } from '@/hooks/useGovernanceVote';
import type { GovernanceVoting, ValidatorSnapshot, VoteResults, VoteStatus } from '@/types/governance';
import SuiCoinIcon from '@/components/icons/sui-coin.png';

interface ValidatorVoteListProps {
    votes: GovernanceVoting['votes'];
    snapshot: ValidatorSnapshot;
    results: VoteResults;
    voteStatus: VoteStatus;
}

// Compact pagination component
function CompactPagination({
    currentPage,
    totalPages,
    onPageChange,
    className = ""
}: {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    className?: string;
}) {
    if (totalPages <= 1) return null;

    return (
        <div className={`flex items-center gap-1 ${className}`}>
            <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
                className="h-8 w-8 p-0"
                title="First page"
            >
                <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="h-8 w-8 p-0"
                title="Previous page"
            >
                <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground px-2 min-w-[60px] text-center">
                {currentPage} of {totalPages}
            </span>
            <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="h-8 w-8 p-0"
                title="Next page"
            >
                <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="h-8 w-8 p-0"
                title="Last page"
            >
                <ChevronsRight className="h-4 w-4" />
            </Button>
        </div>
    );
}

export function ValidatorVoteList({ votes, snapshot, results, voteStatus }: ValidatorVoteListProps) {
    const { data: activeValidators = [] } = useActiveValidators();
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    // Create a map of validator addresses to their names
    const validatorNames = new Map(
        activeValidators.map(v => [v.address, v.name])
    );

    // Create a map of validator addresses to their effective stake
    const effectiveStake = new Map<string, bigint>();

    // Process Record-based stake data instead of array
    for (const [validator_address, stake_amount] of Object.entries(snapshot.all_stake)) {
        const ignored = snapshot.ignored_stake[validator_address] || 0n; // Default to 0n if not in ignored_stake
        effectiveStake.set(
            validator_address,
            stake_amount - ignored // Direct bigint subtraction
        );
    }

    // votes is now a Record<string, 'Yes' | 'No' | 'Abstain'> instead of an array
    // Convert Record.entries to get count and create voteMap
    const voteEntries = Object.entries(votes);
    const voteMap = new Map(voteEntries); // votes is already a Record, so Object.entries gives us [address, vote] pairs

    // Helper function to format percentage display
    const formatStakePercentage = (percentage: number): string => {
        const rounded = Number(percentage.toFixed(2));
        if (rounded === 0 && percentage > 0) {
            return "<0.01%";
        }
        return `${rounded.toFixed(2)}%`;
    };

    // Get all validators (both voted and non-voted) from all_stake Record
    const allValidators = Object.keys(snapshot.all_stake).map((validator_address) => {
        const stake = effectiveStake.get(validator_address) || 0n;
        const vote = voteMap.get(validator_address);
        const name = validatorNames.get(validator_address);

        // Fix percentage calculation by converting to numbers before division to preserve precision
        const stakePercentage = results.totalVotingStake > 0n
            ? (Number(stake) / Number(results.totalVotingStake)) * 100
            : 0;

        return {
            address: validator_address,
            name,
            vote,
            stake,
            stakePercentage
        };
    });

    // Sort by stake amount (descending)
    allValidators.sort((a, b) => Number(b.stake - a.stake));

    // Filter validators based on search query
    const filteredValidators = useMemo(() => {
        if (!searchQuery.trim()) return allValidators;

        const query = searchQuery.toLowerCase();
        return allValidators.filter(validator => {
            const nameMatch = validator.name?.toLowerCase().includes(query);
            const addressMatch = validator.address.toLowerCase().includes(query);
            return nameMatch || addressMatch;
        });
    }, [allValidators, searchQuery]);

    // Calculate pagination
    const totalPages = Math.ceil(filteredValidators.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedValidators = filteredValidators.slice(startIndex, endIndex);

    // Reset to page 1 when search query or page size changes
    useMemo(() => {
        setCurrentPage(1);
    }, [searchQuery, pageSize]);

    const getVoteBadgeColor = (vote?: string) => {
        switch (vote) {
            case 'Yes': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'No': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            case 'Abstain': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            default: return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
        }
    };

    const getVoteDisplayText = (vote?: string) => {
        if (vote) return vote;
        return voteStatus === 'ended' ? "Did not vote" : "Have not voted";
    };

    const notVotedLabel = voteStatus === 'ended' ? "Did not vote" : "Have not voted";

    return (
        <Card className='bg-[#C0E6FF]/10 backdrop-blur-md border border-white/20 rounded-lg shadow-lg'>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 mb-4">
                    <Users className="h-5 w-5" />
                    Validator Votes ({voteEntries.length} of {allValidators.length} voted)
                </CardTitle>

                {/* Vote Summary - updated to work with Record-based votes */}
                <div className="grid grid-cols-4 gap-4 text-center py-4 bg-[#C0E6FF]/5 rounded-lg border border-white/10 mb-4">
                    <div>
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                            {voteEntries.filter(([address, vote]) => vote === 'Yes').length}
                        </div>
                        <div className="text-sm text-muted-foreground">Yes Votes</div>
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                            {voteEntries.filter(([address, vote]) => vote === 'No').length}
                        </div>
                        <div className="text-sm text-muted-foreground">No Votes</div>
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                            {voteEntries.filter(([address, vote]) => vote === 'Abstain').length}
                        </div>
                        <div className="text-sm text-muted-foreground">Abstain</div>
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                            {allValidators.length - voteEntries.length}
                        </div>
                        <div className="text-sm text-muted-foreground">{notVotedLabel}</div>
                    </div>
                </div>

                {/* Search and Controls */}
                <div className="flex flex-col sm:flex-row gap-4 mt-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search by validator name or address..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground whitespace-nowrap">Show:</span>
                            <Select value={pageSize.toString()} onValueChange={(value) => setPageSize(Number(value))}>
                                <SelectTrigger className="w-20">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="10">10</SelectItem>
                                    <SelectItem value="20">20</SelectItem>
                                    <SelectItem value="50">50</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <CompactPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                </div>

                {/* Results info */}
                {searchQuery && (
                    <div className="text-sm text-muted-foreground">
                        Showing {filteredValidators.length} of {allValidators.length} validators
                    </div>
                )}
            </CardHeader>
            <CardContent>
                <div className="space-y-3">
                    {paginatedValidators.map((validator) => (
                        <div
                            key={validator.address}
                            className="flex items-center justify-between p-3 rounded-lg bg-[#C0E6FF]/10 backdrop-blur-md border border-white/20 hover:bg-[#C0E6FF]/20 transition-colors"
                        >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="flex flex-col gap-1 min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        {validator.name && (
                                            <div className="font-medium text-sm truncate">
                                                {validator.name}
                                            </div>
                                        )}
                                        <Badge className={getVoteBadgeColor(validator.vote)}>
                                            {getVoteDisplayText(validator.vote)}
                                        </Badge>
                                    </div>
                                    <AddressDisplay
                                        address={validator.address}
                                        className="text-sm text-muted-foreground"
                                    />
                                </div>
                            </div>

                            <div className="text-right ml-3 flex-shrink-0">
                                <div className="font-medium">
                                    {(Number(validator.stake) / 1e9).toLocaleString()}{' '}
                                    <img src={SuiCoinIcon} alt="SUI" className="inline-block h-4 w-4 ml-1 mt-1 align-top" />
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    {formatStakePercentage(validator.stakePercentage)} of total
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* No results message */}
                {filteredValidators.length === 0 && searchQuery && (
                    <div className="text-center py-8 text-muted-foreground">
                        No validators found matching "{searchQuery}"
                    </div>
                )}

                {/* Bottom Pagination */}
                <div className="mt-6 flex justify-center">
                    <CompactPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </div>
            </CardContent>
        </Card>
    );
} 